// THE ORDER VOCABULARY, AS OPERATOR ENGLISH — what is LEFT OVER after the source was fixed.
//
// The platform's own code now says what it means (PointOfSaleHandoffStateChanged, PointOfSaleHandoffStates,
// OrderEventTypes.SentToPointOfSale), and OrderEventKeys froze the idempotency keys so that naming can move
// freely. What did NOT move is the stored VALUES, and they are why this file still exists:
//   * OrderEvents.Type values ("Dispatched", "XtStatusUpdated", "Notified") are matched inside the guards in
//     front of real side effects, so they are wire contract exactly like the keys;
//   * hand-off state values come from OutboundDispatchStatus.ToString() and land on stored documents;
//   * the Marten aliases ("orders_dispatch_state_changed_v1") are what every stream rehydrates by.
// So the tokens below are the old spellings, still on disk, being read by a screen. Nothing here is stored,
// hashed, sent or matched against, and every label carries its raw token in a tooltip.
//
// AN ENTRY EARNS ITS PLACE, OR IT IS `null`. humaniseOrderToken() already renders 'PickedUp' as "Picked up",
// so an entry restating the mechanical result is a second copy of a rule that works. Those tokens are `null`:
// still listed, because the listing proves the token was CONSIDERED, but carrying no text. Text exists only
// where the mechanical rendering would be wrong ('Dispatched'), misleading ('Delivered' on our hand-off track
// is the till, not the door), or where a hint says what the label cannot.
//
// COMPLETENESS IS ENFORCED: OrderVocabularyCompletenessTests (tests/UnitTests) reads this file as text and
// fails the build on a token the platform emits with no key here — and on a vocabulary nobody classified as
// rendered-here or deliberately-verbatim.
//
// FOREIGN VOCABULARIES ARE ABSENT ON PURPOSE. Magento's statuses and DoorDash's delivery statuses are those
// systems' words about their own state; orders-tab routes only OUR tracks through this map for that reason.

/** One term: what an operator should read, and (when the label alone would lose meaning) why.
 *
 *  `label` is OPTIONAL: omit it when humaniseOrderToken() already produces the right words and the entry
 *  exists only for its hint. */
export interface OrderTerm {
    readonly label?: string;
    readonly hint?: string;
}

// Acronyms the mechanical humaniser must not sentence-case. Keyed by the lower-cased word.
const ACRONYMS: Record<string, string> = {
    xt: 'XT',
    pos: 'POS',
    id: 'ID',
    ids: 'IDs',
    sku: 'SKU',
    url: 'URL',
    sms: 'SMS'
};

// Keys are LOWER-CASED, because the same concept is spelled differently by different layers: the relational
// timeline says 'Xt' and the lifecycle wire says 'xt'; a hand-off row says 'Notified' and an XT activity row
// says 'notified'. One entry serves both.
//
// Where two vocabularies share a token they also share a meaning (Notified, Delivered, Canceled,
// ReadyForPickup, DeliveryStatusChanged, XtOrderIdAssigned, Dispatch, Delivery), so a single flat map is
// correct rather than merely convenient. If that ever stops being true, the collision needs a per-site
// lookup — not a second copy of this file.
//
// `null` MEANS "CHECKED, AND THE HUMANISER IS RIGHT". It is not a gap.
const TERMS: Record<string, OrderTerm | null> = {
    // ---- Our hand-off to the store (PointOfSaleHandoffStates / OutboundDispatchStatus) -------------
    // The recipient here is a SYSTEM - the store Edge, or the legacy XT POS hand-off for older orders - so the
    // terminal state is "the store knows", never "the customer got it".
    pending: { label: 'Waiting to send', hint: 'Queued for the store (or the legacy POS). Nothing has been sent yet.' },
    sent: {
        label: 'Sent to store',
        hint: "In the store Edge's queue, not yet acknowledged by the store (for legacy XT orders: handed to XT, not yet acknowledged)."
    },
    notified: {
        label: 'Store has the order',
        hint: 'The store Edge confirmed it has the order (legacy XT: XT accepted it and returned its own order number). Says nothing about the customer receiving anything.'
    },
    failed: {
        label: 'Store refused it',
        hint: "The store rejected the order, or the legacy POS answered with an error — the refusal itself is in Last error."
    },
    deadlettered: {
        label: 'Gave up sending',
        hint: 'The retries ran out. Nothing further will be attempted automatically; re-send by hand.'
    },
    heldforpayment: {
        label: 'Waiting for payment',
        hint: 'Deliberately not sent: the card has not cleared yet. The send resumes on its own when the money lands.'
    },
    heldbybreaker: {
        label: 'Held — POS down',
        hint: "Deliberately not sent: several different orders failed to reach this point of sale in a row, so its circuit breaker is holding new ones instead of retrying them into the dead-letter queue. Sent automatically once the point of sale answers again; the Order hand-off page shows the breaker."
    },
    abandoned: {
        label: 'Never sent',
        hint: "Retired without a send: the order was canceled before its payment arrived, or it was waiting for the legacy XT hand-off after that was switched off (it then went to the store Edge). Last error says which."
    },

    // ---- Order events on the timeline (OrderEventTypes) ---------------------------------------------
    // The C# names read as domain language now; these are the stored values they still carry.
    dispatched: { label: 'Sent to store', hint: "The order was sent to the store Edge's queue (legacy orders: handed to XT for registration)." },
    dispatchfailed: { label: 'Hand-off failed', hint: 'The attempt to send the order to the store (or register it in the legacy POS) did not land.' },
    dispatchheldbybreaker: {
        label: 'Hand-off held',
        hint: "Not attempted: the point of sale's circuit breaker was open because other orders had been failing. The comment has the breaker's reason; the send resumes on its own."
    },
    xtorderidassigned: {
        // TWO points of sale reach this now - the legacy XT hand-off and the store Edge - and both hand the
        // number back, so the hint names neither. "XT returned" on an order a store numbered would send an
        // operator to the wrong system; the reporter is on the row itself.
        label: 'POS order number assigned',
        hint: "The point of sale returned its own order number, which was handed back to Magento (sales_order.xt_order_id)."
    },
    xtstatusupdated: {
        label: 'POS status updated',
        hint: "The order's fulfilment status (ready / out for delivery / complete) was pushed to XT."
    },
    paymentdetailssynced: {
        label: 'Card details sent to Magento',
        hint: "Card mask, transaction reference and captured amount written onto Magento's payment record."
    },
    deliveryquoteaccepted: {
        label: 'Courier booked',
        hint: 'The courier quote was committed once the payment cleared — the moment a dasher is actually assigned.'
    },
    deliverystatuschanged: { label: 'Courier update', hint: 'The courier told us where the order is.' },
    // A refund is three systems in turn, and each is its own row: Magento's books (the credit memo), the
    // processor's money (the card), and this platform closing it (Refunded).
    refunded: {
        hint: 'The refund is complete: Magento credited the order, the card was refunded where there was card money here, and the store was told when the whole order went back.'
    },
    creditmemoissued: {
        label: 'Magento refund (credit memo)',
        hint: 'Magento issued a credit memo: its books are credited for the refund. No money has moved yet — "Card refunded" follows, then "Refunded" closes the refund.'
    },
    paymentrefunded: {
        label: 'Card refunded',
        hint: 'The payments ledger gave the money back to the card, or released the hold. The amount is on the row; "Refunded" follows.'
    },
    refundfailed: {
        hint: 'A refund was asked for and did not complete. The note says how far it got; a retry resumes there and never issues a second credit memo.'
    },
    shippingupdated: {
        label: 'Shipped',
        hint: 'XT reported the hand-off and the shipment was recorded in Magento.'
    },
    readyforpickup: {
        hint: 'The point of sale said the order is waiting at the counter. Magento emailed the customer that it is ready for pickup and, as its pickup notification does, shipped the order.'
    },
    readyfordelivery: {
        label: 'Ready for delivery',
        hint: 'The point of sale said the delivery is packed and waiting for its driver. The customer was emailed that it is on its way shortly; nothing has shipped yet.'
    },
    shippingupdatefailed: {
        label: 'Magento shipping update failed',
        hint: 'XT reported a hand-off, but Magento would not accept the shipment.'
    },
    shippingupdateskipped: {
        label: 'Magento shipping update skipped',
        hint: 'The order has no invoice yet, so there was nothing to ship against.'
    },
    delivered: {
        label: 'Delivered to customer',
        hint: "The courier's word: the order reached the door. Not the same as the POS having heard of it."
    },
    canceled: null,
    completed: {
        label: 'Order completed',
        hint: 'Magento was moved to its final state. A pickup collected at the till is completed and never delivered.'
    },
    storeorderrecorded: {
        label: 'Store sale reported',
        hint: "The store Edge reported a sale taken at its own counter or kiosk. Recorded here only - it is never sent back to a POS."
    },
    storestatusreported: {
        label: 'Store update',
        hint: "The in-store Edge reported what it did with the order (received, accepted, rejected, ready, completed). The store's own word, recorded as evidence."
    },
    cancellationsenttostore: {
        label: 'Cancellation sent to store',
        hint: "The order was cancelled here, so the store Edge it was sent to was told to stop working on it. The store's own confirmation follows as a store update."
    },
    statussenttostore: {
        label: 'Status sent to store',
        hint: "The order's fulfilment status (ready / out for delivery / shipped / completed) was sent to the store Edge's queue — by a courier webhook or by hand. The store's own word on what it did with it follows as a store update."
    },

    // ---- Fulfillment stages (FulfillmentStages / OrderFulfillmentStages) ----------------------------
    // ReadyForPickup / Delivered / Canceled are shared with the event types above.
    shipped: null,
    pickedup: null,

    // ---- Operational facts (OperationalFacts / OrderOperations) -------------------------------------
    snapshotrefetched: { label: 'Re-read from Magento', hint: 'The order was fetched again from its source system.' },
    webhooksreplayed: { hint: "The courier's stored callbacks were re-applied. Idempotent — an update already applied is skipped." },
    refundrequested: null,
    ordersuspended: null,
    paymentconfirmed: { hint: 'The card cleared on this platform.' },
    ordercanceled: null,
    ticketreprintqueued: {
        label: 'Re-print queued',
        hint: "Queued for the store's printer again; it prints on the station's next poll."
    },
    ticketprinted: { hint: "The store's print station fetched and printed the ticket." },

    // ---- Lifecycle fact kinds (OrderLifecycleKinds) --------------------------------------------------
    dispatchstatechanged: { label: 'POS hand-off changed', hint: 'Our own send-to-POS state machine moved.' },
    shipmentrecorded: { hint: 'Some of this order is on its way. An order can ship in several parts.' },
    trackingassigned: {
        label: 'Tracking number assigned',
        hint: 'Tracking arrived, or changed, after the shipment was recorded — carriers issue it after pickup.'
    },
    fulfillmentadvanced: { label: 'Fulfillment stage advanced' },
    operationrecorded: { label: 'Order operation', hint: 'Something the platform did with the order that carries no payload of its own.' },

    // ---- Timeline categories (TimelineCategories) ----------------------------------------------------
    snapshot: {
        label: 'Source change',
        hint: 'A difference between two readings of the order in its source system.'
    },
    lifecycle: {
        label: 'Platform action',
        hint: 'Something this platform did with the order — a POS hand-off, a courier update, a printed ticket.'
    },

    // ---- Timeline change types (TimelineChangeTypes) -------------------------------------------------
    received: { label: 'Order received' },
    status: { label: 'Order status' },
    totals: null,
    shipping: { label: 'Shipping method' },
    items: null,
    payments: null,
    refunds: null,
    customer: { label: 'Customer details' },
    field: null,
    dispatch: { label: 'Store hand-off', hint: 'Our send of the order to the store Edge (legacy: XT) — not the courier, and not the customer.' },
    delivery: { label: 'Courier' },
    shipment: null,
    tracking: null,
    xtorderid: { label: 'POS order number' },
    printed: { label: 'Ticket printed' },
    operation: null,
    fulfillment: null,

    // ---- Status-history tracks (StatusTracks) -------------------------------------------------------
    // 'Delivery' and 'Dispatch' are shared with the change types above; only 'Order' is track-only.
    order: { label: 'Order status', hint: "The source system's own status for the order." },

    // ---- Who reported it / where it came from (OrderReporters, OrderEventSources, OrderSources) -----
    xt: { label: 'POS (XT, legacy)', hint: 'The legacy POS hand-off. New online orders go to the store Edge.' },
    edge: {
        label: 'Store (Edge)',
        hint: "The store's in-store Edge: a sale taken at its own counter or kiosk, or the store's word on an online order it was sent."
    },
    doordash: { label: 'DoorDash' },
    magento: null,
    uber: null,
    central: { label: 'This platform' },
    internal: { label: 'This platform' },

    // ---- Per-event outcome tokens -------------------------------------------------------------------
    // Free-form lower-case statuses written beside an order event — inline literals, not constants, which is
    // why the completeness test cannot reach them and why they are spelled out here. The '-failed' variants
    // are composed server-side ($"{label}-failed"), so the suffix rule in orderTerm() covers any new ones.
    synced: { label: 'Handed back to Magento' },
    'sync-failed': { label: 'Magento hand-back failed' },
    accepted: null,
    'accept-failed': { label: 'Courier refused the booking' },
    advanced: { label: 'Applied in Magento' },
    skipped: null,
    ready: { label: 'Ready for pickup' },
    out: { label: 'Out for delivery' },
    complete: { label: 'Complete in POS' },
    'complete-failed': { label: 'POS refused the completion' },
    'cancel-failed': { label: 'Magento refused the cancel' },
    // A refund made at the register (HostOrderRefund): the credit memo's own outcome, and a refund that stopped.
    issued: null,
    'refund-failed': { label: 'Refund did not complete' },
    // The store Edge's own statuses (BuildingBlocks.StoreContracts.StoreOrderStatuses), written beside a
    // StoreStatusReported event and held to the ledger like any declared vocabulary. 'accepted', 'ready' and
    // 'completed' are shared with the tokens above and read correctly there.
    acknowledged: { label: 'Store has the order', hint: 'The store Edge confirmed it received the order. Sent automatically on arrival.' },
    rejected: { label: 'Store rejected the order' },
    in_preparation: { label: 'Being prepared' },
    cancelled: { label: 'Canceled at the store' },
    // What the middleware pushes DOWN to the store (BuildingBlocks.StoreContracts.StoreFulfilmentStatuses), beside a
    // StatusSentToStore event. 'ready', 'shipped' and 'completed' are shared with tokens above and read correctly there.
    out_for_delivery: { label: 'Out for delivery', hint: 'A courier has the order and is on the way to the customer.' },
    // A store sale's own states (BuildingBlocks.StoreContracts.StoreSaleStatuses), beside a StoreOrderRecorded event.
    // 'refunded' is shared with the event type above and reads correctly there.
    committed: { label: 'Rung up', hint: 'Committed at the store counter; nothing paid yet.' },
    partially_paid: { label: 'Partly paid' },
    paid: null,
    voided: null,

    // ---- Stored Marten stream aliases (OrdersInfrastructure MapEventType) --------------------------
    // The Events tab shows the raw stream. These are the stored aliases verbatim — the strings whose
    // stability every rehydration depends on, which is exactly why they are relabelled here and nowhere near
    // the store. orderEventAliasLabel() strips the 'orders_' prefix and '_v1' suffix before humanising, so
    // most of them already read correctly and are `null`; only the four whose mechanical rendering would be
    // wrong or jargon carry text.
    orders_received_v1: { label: 'Order received' },
    orders_state_recorded_v1: { label: 'Snapshot recorded', hint: 'Where a change set landed — the full canonical order.' },
    orders_status_changed_v1: { label: 'Order status changed' },
    orders_totals_changed_v1: null,
    orders_shipping_method_changed_v1: null,
    orders_items_changed_v1: null,
    orders_payments_changed_v1: null,
    orders_refunds_changed_v1: null,
    orders_customer_changed_v1: { label: 'Customer details changed' },
    orders_field_changed_v1: null,
    orders_dispatch_state_changed_v1: { label: 'POS hand-off changed' },
    orders_delivery_status_changed_v1: { label: 'Courier update' },
    orders_shipment_recorded_v1: null,
    orders_tracking_assigned_v1: { label: 'Tracking number assigned' },
    orders_fulfillment_advanced_v1: { label: 'Fulfillment stage advanced' },
    orders_xt_order_id_assigned_v1: { label: 'POS order number assigned' },
    orders_ticket_printed_v1: null,
    orders_operation_recorded_v1: null
};

/** Split a PascalCase / snake_case / kebab-case token into words and sentence-case it, keeping known
 *  acronyms upper. 'HeldForPayment' -> 'Held for payment', 'dropped_off' -> 'Dropped off',
 *  'XtOrderIdAssigned' -> 'XT order ID assigned'. This is the DEFAULT rendering, not merely a fallback:
 *  every `null` entry above is a token this rule renders correctly, and for a token the file has never heard
 *  of it degrades to a tidier version of ITSELF — never a confident wrong name and never blank. */
export function humaniseOrderToken(token: string | null | undefined): string {
    const raw = (token ?? '').trim();
    if (!raw) return '';

    const words = raw
        // PascalCase / camelCase boundaries, including the ACRONYMBoundary case ('XTOrder' -> 'XT Order').
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .split(/[\s_\-.]+/)
        .filter((w) => w.length > 0);
    if (words.length === 0) return raw;

    return words
        .map((word, i) => {
            const lower = word.toLowerCase();
            const acronym = ACRONYMS[lower];
            if (acronym) return acronym;
            return i === 0
                ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                : lower;
        })
        .join(' ');
}

/** The curated term for a token, or null when there is nothing curated to say about it — which covers both
 *  "listed above as mechanically correct" and "never heard of it". Callers that only want text should use
 *  orderLabel(), which never returns null; the two cases are deliberately indistinguishable here because
 *  they render identically.
 *
 *  Composed '{something}-failed' statuses fall back to the base term so a status invented server-side
 *  tomorrow still reads as a failure of a thing we can name. */
export function orderTerm(token: string | null | undefined): OrderTerm | null {
    const raw = (token ?? '').trim();
    if (!raw) return null;

    const direct = TERMS[raw.toLowerCase()];
    if (direct) return direct;

    const failed = /^(.+)-failed$/.exec(raw.toLowerCase());
    if (failed) {
        const base = TERMS[failed[1]];
        return { label: `${base?.label ?? humaniseOrderToken(failed[1])} — failed`, hint: base?.hint };
    }

    return null;
}

/** The label an operator reads. Empty token in, empty string out: "the source told us nothing" is the
 *  caller's own sentence to write, and it differs per field. */
export function orderLabel(token: string | null | undefined): string {
    const raw = (token ?? '').trim();
    if (!raw) return '';
    return orderTerm(raw)?.label ?? humaniseOrderToken(raw);
}

/** The tooltip that keeps this honest: the meaning where there is one, and ALWAYS the raw token, so an
 *  operator reads the label and an engineer still debugs by token. Suppressed only when the label IS the
 *  token, where repeating it would be noise. */
export function orderTooltip(token: string | null | undefined): string {
    const raw = (token ?? '').trim();
    if (!raw) return '';
    const hint = orderTerm(raw)?.hint;
    const sameAsLabel = orderLabel(raw).toLowerCase() === raw.toLowerCase();
    if (hint) return sameAsLabel ? hint : `${hint} · token: ${raw}`;
    return sameAsLabel ? '' : `token: ${raw}`;
}

/** A stored Marten stream alias -> label. The storage decoration (the 'orders_' prefix and the '_v1' version
 *  suffix) is stripped before humanising, which is what lets most aliases carry no curated text at all and a
 *  future 'orders_foo_happened_v2' read "Foo happened" rather than "Orders foo happened v2". */
export function orderEventAliasLabel(alias: string | null | undefined): string {
    const raw = (alias ?? '').trim();
    if (!raw) return '';
    const curated = orderTerm(raw)?.label;
    if (curated) return curated;
    return humaniseOrderToken(raw.replace(/^orders?_/, '').replace(/_v\d+$/, '')) || raw;
}

/** Dropdown options for tokens harvested off a loaded page: relabelled, with the RAW token as the value
 *  because that is what the query string carries. Sorted by what the reader sees. */
export function orderTokenOptions(tokens: Iterable<string>): { label: string; value: string }[] {
    return [...new Set(tokens)]
        .filter((t) => !!t)
        .map((t) => ({ label: orderLabel(t), value: t }))
        .sort((a, b) => a.label.localeCompare(b.label));
}
